// Registers the PWA service worker. Detects a stale SW version and forces a
// one-time clean refresh so users stuck on an old cached bundle recover automatically.
// Shows a minimal, zero-dependency toast during the recovery/update window.

const EXPECTED_SW_VERSION = 'fmaa-v5';
const LS_KEY = 'fmaa_sw_version';

function showUpdateToast(message = 'Updating to the latest version…') {
  try {
    if (document.getElementById('fmaa-sw-toast')) return;
    const el = document.createElement('div');
    el.id = 'fmaa-sw-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('data-testid', 'sw-update-toast');
    el.style.cssText = [
      'position:fixed',
      'bottom:24px',
      'left:50%',
      'transform:translateX(-50%)',
      'z-index:100000',
      'background:#163300',
      'color:#fff',
      'padding:12px 20px',
      'border-radius:999px',
      'font-family:ui-monospace,SFMono-Regular,Menlo,monospace',
      'font-size:13px',
      'letter-spacing:0.02em',
      'box-shadow:0 10px 30px rgba(0,0,0,0.18)',
      'display:flex',
      'align-items:center',
      'gap:10px',
      'opacity:0',
      'transition:opacity 260ms ease',
    ].join(';');
    el.innerHTML =
      '<span style="display:inline-block;width:14px;height:14px;border:2px solid #fff6;border-top-color:#fff;border-radius:50%;animation:fmaa-sw-spin 0.9s linear infinite"></span>' +
      '<span>' + message + '</span>';
    const style = document.createElement('style');
    style.textContent = '@keyframes fmaa-sw-spin{to{transform:rotate(360deg)}}';
    document.head.appendChild(style);
    document.body.appendChild(el);
    requestAnimationFrame(() => { el.style.opacity = '1'; });
  } catch (e) { /* non-fatal */ }
}

export function registerServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') return;

  // If browser was previously controlled by an older SW version, nuke caches once.
  const lastSeen = window.localStorage.getItem(LS_KEY);
  if (lastSeen && lastSeen !== EXPECTED_SW_VERSION) {
    showUpdateToast('Updating to the latest version…');
    (async () => {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
        window.localStorage.setItem(LS_KEY, EXPECTED_SW_VERSION);
        // One-time reload so the uncached new bundle loads fresh
        if (!sessionStorage.getItem('fmaa_sw_reloaded')) {
          sessionStorage.setItem('fmaa_sw_reloaded', '1');
          setTimeout(() => window.location.reload(), 600);
        }
      } catch (e) {
        /* ignore */
      }
    })();
    return;
  }

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        window.localStorage.setItem(LS_KEY, EXPECTED_SW_VERSION);
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              showUpdateToast('A new version is ready — refreshing…');
              newWorker.postMessage('SKIP_WAITING');
            }
          });
        });
      })
      .catch(() => {});

    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return;
      reloading = true;
      setTimeout(() => window.location.reload(), 400);
    });
  });
}
